import { z } from 'zod';

import { apiRequest } from '@/api/client';

/**
 * How a deal card is drawn: the text alone, a banner picture over the text, or
 * the picture alone. An image-only card still draws the code and the apply
 * button under the picture - a code baked into a picture cannot be copied.
 */
export const DEAL_DISPLAY_MODES = ['TEXT', 'IMAGE_TEXT', 'IMAGE'] as const;
export type DealDisplayMode = (typeof DEAL_DISPLAY_MODES)[number];

export const OFFER_STRIP_DIRECTIONS = ['LEFT_TO_RIGHT', 'RIGHT_TO_LEFT'] as const;
export const OFFER_STRIP_SPEEDS = ['SLOW', 'NORMAL', 'FAST'] as const;

/**
 * How the marketing header's strip is drawn, set on the plans screen under
 * Signing up. Mirrors `OfferStripStyle` in `app/schemas/partner.py`.
 */
export const offerStripStyleSchema = z.object({
  /** `#rrggbb`; the text colour is chosen against it for contrast. */
  background: z.string(),
  scroll: z.boolean(),
  direction: z.enum(OFFER_STRIP_DIRECTIONS).catch('LEFT_TO_RIGHT'),
  speed: z.enum(OFFER_STRIP_SPEEDS).catch('NORMAL'),
});

export type OfferStripStyle = z.infer<typeof offerStripStyleSchema>;

/**
 * Today's Deals: the platform's own live, public promo codes.
 *
 * Mirrors `PublicDeal` in `app/schemas/partner.py`. `remaining` is how many
 * places are left on a capped deal, and null when the deal has no cap.
 */
export const publicDealSchema = z.object({
  code: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  terms: z.string().nullable(),
  badge: z.string().nullable(),
  is_featured: z.boolean(),
  /** The deal the marketing header strip shows; at most one is set. */
  show_in_strip: z.boolean().catch(false),
  /** Only on the strip deal: the owner's colour and motion for the strip. */
  strip_style: offerStripStyleSchema.nullable().optional().catch(null),
  /** The same sentence the signup form shows once the code is typed. */
  benefit: z.string().nullable(),
  plan_codes: z.array(z.string()).nullable(),
  /** Rupees off per billing cycle (`{ YEARLY: "1000.00" }`), or null. */
  interval_values: z.record(z.string(), z.string()).nullable().catch(null),
  starts_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  remaining: z.number().nullable(),
  display_mode: z.enum(DEAL_DISPLAY_MODES).catch('TEXT'),
  /** Set only on the image cards; alt falls back to the title server-side. */
  image_url: z.string().nullable().catch(null),
  image_alt: z.string().nullable().catch(null),
});

export type PublicDeal = z.infer<typeof publicDealSchema>;

export function fetchTodaysDeals(): Promise<PublicDeal[]> {
  return apiRequest('/deals/today', z.array(publicDealSchema), {
    // Read by visitors without an account, so a 401 is not worth a refresh.
    skipAuthRefresh: true,
  });
}
